/**
 * `/checkout` — el checkout.
 *
 * Los métodos de pago y de envío salen de la API: si la API deshabilita un
 * método, no aparece acá. La fila del descuento nombra el método con
 * descuento **realmente habilitado**, no uno fijo.
 *
 * El formulario es un `GET` a `/cuenta/pedidos`: es lo que hace el legacy y
 * lo que el backend expone hoy. Cuando exista `POST /api/pedidos`, este
 * form pasa a llamar a la API.
 */
'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import { precio } from '@/lib/format';
import type { Producto } from '@/lib/tipos';
import type { MetodoPago, PuntoRetiro } from '@/lib/tipos';

export interface OpcionEnvio {
  id: string;
  label: string;
  description: string;
  /** Envío gratis por zona (no por método). */
  freeByZone?: boolean;
}

export interface Props {
  productos: Producto[];
  metodosPago: MetodoPago[];
  metodosEnvio: OpcionEnvio[];
  retiro: PuntoRetiro[];
  carrierNote: string;
  freeZonesNote: string;
  etiquetaImpuestos: string;
  garantiaTexto: string;
  nombresMetodosConDescuento: string;
  maxDiscountPercent: number;
}

const CLAVE_CARRITO = 'joyeria.carrito';

interface Linea {
  clave: string;
  slug: string;
  idVariante?: string;
  cantidad: number;
}

function leerCarrito(): Linea[] {
  if (typeof window === 'undefined') return [];
  try {
    const crudo = window.localStorage.getItem(CLAVE_CARRITO);
    if (!crudo) return [];
    const datos = JSON.parse(crudo) as Record<string, number>;
    return Object.entries(datos).map(([clave, cantidad]) => {
      const [slug, idVariante] = clave.split(':');
      return { clave, slug, idVariante, cantidad };
    });
  } catch {
    return [];
  }
}

export function CheckoutView({
  productos,
  metodosPago,
  metodosEnvio,
  retiro,
  carrierNote,
  freeZonesNote,
  etiquetaImpuestos,
  garantiaTexto,
  nombresMetodosConDescuento,
  maxDiscountPercent,
}: Props) {
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [pago, setPago] = useState(metodosPago[0]?.id ?? '');
  const [envio, setEnvio] = useState(metodosEnvio[0]?.id ?? '');
  const [ciudad, setCiudad] = useState('');
  const [listo, setListo] = useState(false);

  useEffect(() => {
    setLineas(leerCarrito());
    setListo(true);
  }, []);

  const resueltas = lineas
    .map((linea) => {
      const producto = productos.find((p) => p.slug === linea.slug);
      if (!producto) return null;
      const variante = linea.idVariante
        ? producto.variantes.find((v) => v.id === linea.idVariante)
        : null;
      const unitario = variante?.precio ?? producto.precio;
      return { ...linea, producto, unitario, total: unitario * linea.cantidad };
    })
    .filter((linea): linea is NonNullable<typeof linea> => linea !== null);

  const subtotal = resueltas.reduce((acc, linea) => acc + linea.total, 0);

  // El descuento: sólo si el método elegido tiene descuento y la API lo
  // habilitó. Nunca se anuncia un método que no existe.
  const metodoElegido = metodosPago.find((m) => m.id === pago);
  const descuento = metodoElegido?.discountPercent
    ? Math.round(subtotal * (metodoElegido.discountPercent / 100))
    : 0;

  // El envío gratis depende de la zona y del método de envío elegido.
  const opcionEnvio = metodosEnvio.find((o) => o.id === envio);
  const envioGratis = Boolean(opcionEnvio?.freeByZone) && ciudad.trim().length > 0;

  const total = Math.max(0, subtotal - descuento);

  return (
    <div className="container checkout-layout">
      <form data-checkout-form action="/cuenta/pedidos" method="get">
        <fieldset className="fieldset">
          <legend className="fieldset__legend">Tus datos</legend>
          <div className="field">
            <label className="field__label" htmlFor="co-nombre">
              Nombre y apellido
            </label>
            <input className="field__input" id="co-nombre" name="nombre" type="text" autoComplete="name" required />
          </div>
          <div className="field-row">
            <div className="field">
              <label className="field__label" htmlFor="co-email">
                Email
              </label>
              <input className="field__input" id="co-email" name="email" type="email" autoComplete="email" required />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="co-telefono">
                Teléfono
              </label>
              <input className="field__input" id="co-telefono" name="telefono" type="tel" autoComplete="tel" required />
            </div>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="co-dni">
              DNI
            </label>
            <input className="field__input" id="co-dni" name="dni" type="text" inputMode="numeric" required />
          </div>
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset__legend">Envío</legend>
          {metodosEnvio.map((opcion) => (
            <label className="radio-card" key={opcion.id}>
              <input
                type="radio"
                name="envio"
                value={opcion.id}
                checked={envio === opcion.id}
                onChange={() => setEnvio(opcion.id)}
                {...(opcion.freeByZone ? { 'data-gratis-por-zona': 'true' } : {})}
              />
              <span>
                <span className="radio-card__name">{opcion.label}</span>
                <span className="radio-card__desc">{opcion.description}</span>
              </span>
            </label>
          ))}

          <div className="field-row" style={{ marginTop: 12 }}>
            <div className="field">
              <label className="field__label" htmlFor="co-direccion">
                Dirección
              </label>
              <input
                className="field__input"
                id="co-direccion"
                name="direccion"
                type="text"
                autoComplete="street-address"
                required
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="co-cp">
                Código postal
              </label>
              <input
                className="field__input"
                id="co-cp"
                name="cp"
                type="text"
                inputMode="numeric"
                autoComplete="postal-code"
                required
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label className="field__label" htmlFor="co-ciudad">
                Ciudad
              </label>
              <input
                className="field__input"
                id="co-ciudad"
                name="ciudad"
                type="text"
                autoComplete="address-level2"
                value={ciudad}
                onChange={(evento) => setCiudad(evento.target.value)}
                required
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="co-provincia">
                Provincia
              </label>
              <input
                className="field__input"
                id="co-provincia"
                name="provincia"
                type="text"
                autoComplete="address-level1"
                required
              />
            </div>
          </div>

          {retiro.length > 0 && (
            <p className="summary__note" style={{ marginTop: 12 }}>
              También podés retirar en:{' '}
              {retiro.map((punto) => `${punto.nombre} (${punto.ciudad})`).join(', ')}.
            </p>
          )}
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset__legend">Pago</legend>
          {metodosPago.map((metodo) => (
            <label className="radio-card" key={metodo.id}>
              <input
                type="radio"
                name="pago"
                value={metodo.id}
                checked={pago === metodo.id}
                onChange={() => setPago(metodo.id)}
                {...(metodo.discountPercent
                  ? { 'data-discount': String(metodo.discountPercent) }
                  : {})}
              />
              <span>
                <span className="radio-card__name">{metodo.name}</span>
                <span className="radio-card__desc">{metodo.description}</span>
              </span>
            </label>
          ))}
          <p className="summary__note">
            {maxDiscountPercent > 0 && metodosPago.some((m) => m.discountPercent)
              ? `Descuento de ${maxDiscountPercent}% en ${nombresMetodosConDescuento}. `
              : ''}
            {carrierNote}
          </p>
        </fieldset>

        <button className="btn btn--block" type="submit">
          Confirmar pedido
        </button>
        <p className="summary__note">{garantiaTexto}</p>
      </form>

      <aside className="summary" aria-labelledby="resumen-checkout">
        <p className="summary__title" id="resumen-checkout">
          Resumen
        </p>
        <div data-checkout-lines>
          {listo && resueltas.length === 0 ? (
            <p className="empty-state">Tu carrito está vacío.</p>
          ) : (
            resueltas.map((linea) => (
              <div className="summary__row" key={linea.clave}>
                <span>
                  {linea.producto.nombre} × {linea.cantidad}
                </span>
                <span>{precio(linea.total)}</span>
              </div>
            ))
          )}
        </div>
        <div className="summary__row" style={{ marginTop: 14 }}>
          <span>Subtotal</span>
          <span data-checkout-subtotal>{precio(subtotal)}</span>
        </div>
        {descuento > 0 && (
          <div className="summary__row" data-checkout-discount-row>
            <span>{nombresMetodosConDescuento}</span>
            <span className="summary__discount" data-checkout-discount>
              −{precio(descuento)}
            </span>
          </div>
        )}
        <div className="summary__row">
          <span>Envío</span>
          <span data-checkout-shipping>
            {envioGratis ? 'Gratis' : 'A calcular'}
          </span>
        </div>
        <div className="summary__row summary__row--total">
          <span>Total</span>
          <span data-checkout-total>{precio(total)}</span>
        </div>
        <p className="summary__note">
          {etiquetaImpuestos}. {freeZonesNote}
        </p>
        <p className="summary__note" style={{ marginTop: 8 }}>
          <Link className="section-link" href="/carrito">
            Volver al carrito
          </Link>
        </p>
      </aside>
    </div>
  );
}
